import { useEffect, useMemo, useRef, useState } from 'react'
import { TIER_IDS, type TierId } from '../../../config/PricingConfig'
import {
  archiveOwnerCohort,
  estimateOwnerCohort,
  getOwnerCohort,
  listOwnerCohorts,
  previewOwnerCohortMembers,
  upsertOwnerCohort,
  type CohortDetailResult,
  type CohortListItem,
  type CohortRule,
  type CohortRuleField,
  type CohortRuleOp,
} from '../../../services/ownerConsoleService'
import { useNls } from '../../../nls/nlsProvider'
import type { NlsKey } from '../../../nls/strings_nls'
import { useRoles, type OwnerRole } from '../context/RolesContext'
import { isSessionStaleError, useSessionLease } from '../context/SessionLeaseProvider'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import { OwnerFirmConfirm } from '../kit/OwnerConfirm'
import { OwnerEmptyScaffold } from '../kit/OwnerEmptyScaffold'
import { navigateOwner, type OwnerRoute } from '../ownerRouter'
import './ownerCohorts.css'

const FIELD_ALLOWED_OPS: Record<CohortRuleField, CohortRuleOp[]> = {
  plan: ['eq', 'in'],
  signupAgeDays: ['lte', 'gte'],
  lastActiveDays: ['lte', 'gte'],
  openTicketCount: ['eq', 'lte', 'gte'],
  workspacesOwnedCount: ['eq', 'lte', 'gte'],
  personalCredits: ['eq', 'lte', 'gte'],
  hasRestrictions: ['eq'],
}

const ALL_FIELDS: CohortRuleField[] = [
  'plan',
  'signupAgeDays',
  'lastActiveDays',
  'openTicketCount',
  'workspacesOwnedCount',
  'personalCredits',
  'hasRestrictions',
]

const TIER_LABEL_KEYS: Record<TierId, NlsKey> = {
  FREE_TIER: 'NLSOwnerConsoleCohortsPlanFree',
  ESSENTIAL_TIER: 'NLSOwnerConsoleCohortsPlanEssential',
  GROWTH_TIER: 'NLSOwnerConsoleCohortsPlanGrowth',
  PROFESSIONAL_TIER: 'NLSOwnerConsoleCohortsPlanProfessional',
  ENTERPRISE_TIER: 'NLSOwnerConsoleCohortsPlanEnterprise',
}

function isRuleComplete(rule: CohortRule): boolean {
  if (!rule || !rule.field || !rule.op) return false
  if (rule.field === 'plan') {
    if (rule.op === 'eq') {
      return typeof rule.value === 'string' && rule.value.trim().length > 0
    }
    if (rule.op === 'in') {
      return Array.isArray(rule.value) && rule.value.length > 0 && rule.value.every((v) => typeof v === 'string' && v.trim().length > 0)
    }
    return false
  }
  if (rule.field === 'hasRestrictions') {
    return typeof rule.value === 'boolean'
  }
  return typeof rule.value === 'number' && Number.isFinite(rule.value) && rule.value >= 0
}

function parseEmailsText(text: string): string[] {
  return text
    .split('\n')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean)
}

function sanitizeCohortRules(rules: CohortRule[]): CohortRule[] {
  return rules.map((r) => {
    if (r.field === 'hasRestrictions') {
      return { field: r.field, op: 'eq', value: Boolean(r.value) }
    }
    if (r.field === 'plan') {
      if (r.op === 'in') {
        const val = Array.isArray(r.value)
          ? r.value.map((s) => String(s).trim()).filter(Boolean)
          : String(r.value || '').split(',').map((s) => s.trim()).filter(Boolean)
        return { field: r.field, op: r.op, value: val }
      }
      return { field: r.field, op: 'eq', value: String(r.value || '').trim() }
    }
    return { field: r.field, op: r.op, value: Number(r.value) || 0 }
  })
}

const formatRuleInWords = (rule: CohortRule, t: ReturnType<typeof useNls>['t']): string => {
  const { field, op, value } = rule
  switch (field) {
    case 'plan':
      if (op === 'eq') {
        const tierKey = typeof value === 'string' && value in TIER_LABEL_KEYS ? TIER_LABEL_KEYS[value as TierId] : undefined
        const label = tierKey ? t(tierKey) : String(value ?? '')
        return t('NLSOwnerConsoleCohortsSummaryRulePlanEq', { value: label })
      }
      {
        const vals = Array.isArray(value) ? value : []
        const labels = vals.map((v) => {
          const tierKey = typeof v === 'string' && v in TIER_LABEL_KEYS ? TIER_LABEL_KEYS[v as TierId] : undefined
          return tierKey ? t(tierKey) : String(v)
        })
        return t('NLSOwnerConsoleCohortsSummaryRulePlanIn', { value: labels.join(', ') })
      }
    case 'signupAgeDays':
      if (op === 'lte') return t('NLSOwnerConsoleCohortsSummaryRuleSignupAgeLte', { value: String(value ?? 0) })
      return t('NLSOwnerConsoleCohortsSummaryRuleSignupAgeGte', { value: String(value ?? 0) })
    case 'lastActiveDays':
      if (op === 'lte') return t('NLSOwnerConsoleCohortsSummaryRuleLastActiveLte', { value: String(value ?? 0) })
      return t('NLSOwnerConsoleCohortsSummaryRuleLastActiveGte', { value: String(value ?? 0) })
    case 'openTicketCount':
      if (op === 'eq') return t('NLSOwnerConsoleCohortsSummaryRuleTicketsEq', { value: String(value ?? 0) })
      if (op === 'lte') return t('NLSOwnerConsoleCohortsSummaryRuleTicketsLte', { value: String(value ?? 0) })
      return t('NLSOwnerConsoleCohortsSummaryRuleTicketsGte', { value: String(value ?? 0) })
    case 'workspacesOwnedCount':
      if (op === 'eq') return t('NLSOwnerConsoleCohortsSummaryRuleWorkspacesEq', { value: String(value ?? 0) })
      if (op === 'lte') return t('NLSOwnerConsoleCohortsSummaryRuleWorkspacesLte', { value: String(value ?? 0) })
      return t('NLSOwnerConsoleCohortsSummaryRuleWorkspacesGte', { value: String(value ?? 0) })
    case 'personalCredits':
      if (op === 'eq') return t('NLSOwnerConsoleCohortsSummaryRuleCreditsEq', { value: String(value ?? 0) })
      if (op === 'lte') return t('NLSOwnerConsoleCohortsSummaryRuleCreditsLte', { value: String(value ?? 0) })
      return t('NLSOwnerConsoleCohortsSummaryRuleCreditsGte', { value: String(value ?? 0) })
    case 'hasRestrictions':
      return value ? t('NLSOwnerConsoleCohortsSummaryRuleRestrictionsTrue') : t('NLSOwnerConsoleCohortsSummaryRuleRestrictionsFalse')
    default:
      return `${field} ${op} ${String(value)}`
  }
}

const formatCohortSummaryInWords = (cohort: CohortListItem, t: ReturnType<typeof useNls>['t']): string => {
  const rules = cohort.rules || []
  if (rules.length === 0) {
    if (cohort.match === 'all') {
      return t('NLSOwnerConsoleCohortsSummaryAllUsers')
    }
    if (cohort.manualCount > 0) {
      return t('NLSOwnerConsoleCohortsSummaryManualOnly', { count: cohort.manualCount })
    }
    return t('NLSOwnerConsoleCohortsSummaryNoOneYet')
  }
  const prefix = cohort.match === 'all'
    ? t('NLSOwnerConsoleCohortsSummaryMatchAllPrefix')
    : t('NLSOwnerConsoleCohortsSummaryMatchAnyPrefix')
  const ruleTexts = rules.map((r) => formatRuleInWords(r, t))
  let summary = prefix + ruleTexts.join('; ')
  if (cohort.manualCount > 0) {
    summary += t('NLSOwnerConsoleCohortsSummaryManualSuffix', { count: cohort.manualCount })
  }
  return summary
}

function OwnerCohortEditor({
  cohortId,
  isNew,
  role,
  canEdit,
  triggerSessionStale,
}: {
  cohortId: string | null
  isNew: boolean
  role: OwnerRole
  canEdit: boolean
  triggerSessionStale: () => void
}) {
  const { t } = useNls()

  const [detailCohort, setDetailCohort] = useState<CohortDetailResult | null>(null)
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [editorErrorMessage, setEditorErrorMessage] = useState<string | null>(null)
  const [editorSuccessMessage, setEditorSuccessMessage] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [match, setMatch] = useState<'all' | 'any'>('all')
  const [rules, setRules] = useState<CohortRule[]>([])
  const [emailsText, setEmailsText] = useState('')
  const [isBuiltIn, setIsBuiltIn] = useState(false)

  // Estimate state
  const [estimatedCount, setEstimatedCount] = useState<string | null>(null)
  const [estimateErrorMessage, setEstimateErrorMessage] = useState<string | null>(null)
  const [isEstimating, setIsEstimating] = useState(false)
  const estimateSeq = useRef(0)

  // Confirm dialogs
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false)
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isArchiving, setIsArchiving] = useState(false)

  // Preview modal
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [previewMembers, setPreviewMembers] = useState<Array<{ email: string; plan: string }>>([])
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)

  useEffect(() => {
    if (isNew || !cohortId) return
    let active = true
    const run = async () => {
      setIsDetailLoading(true)
      try {
        const res = await getOwnerCohort({ id: cohortId })
        if (!active) return
        const cohort = res.cohort
        setDetailCohort(cohort)
        setName(cohort.name)
        setDescription(cohort.description || '')
        setMatch(cohort.match || 'all')
        setRules(cohort.rules || [])
        setEmailsText((cohort.includeEmails || []).join('\n'))
        setIsBuiltIn(Boolean(cohort.builtIn))
        setEstimatedCount(cohort.capped ? t('NLSOwnerConsoleCohortsLiveCountCapped') : String(cohort.memberCount))
      } catch (err) {
        if (!active) return
        if (isSessionStaleError(err)) {
          triggerSessionStale()
        }
        setEditorErrorMessage(t('NLSOwnerConsoleCohortsErrorLoadDetail'))
      } finally {
        if (active) {
          setIsDetailLoading(false)
        }
      }
    }
    void run()
    return () => {
      active = false
    }
  }, [cohortId, isNew, triggerSessionStale, t])

  const hasIncompleteRules = useMemo(() => rules.some((r) => !isRuleComplete(r)), [rules])

  useEffect(() => {
    if (!canEdit) return
    if (!isNew && !detailCohort) return
    if (hasIncompleteRules) {
      return
    }

    const seq = ++estimateSeq.current

    const timer = setTimeout(async () => {
      setIsEstimating(true)
      setEstimateErrorMessage(null)
      try {
        const emails = parseEmailsText(emailsText)
        const sanitizedRules = sanitizeCohortRules(rules)

        const res = await estimateOwnerCohort({
          id: isNew ? undefined : (cohortId || undefined),
          name: name.trim() || undefined,
          description: description.trim() || undefined,
          match,
          rules: sanitizedRules,
          includeEmails: emails.length > 0 ? emails : undefined,
        })
        if (seq === estimateSeq.current) {
          setEstimatedCount(res.capped ? t('NLSOwnerConsoleCohortsLiveCountCapped') : String(res.memberCount))
          setIsEstimating(false)
        }
      } catch (err) {
        if (seq === estimateSeq.current) {
          setIsEstimating(false)
          setEstimatedCount(null)
          if (isSessionStaleError(err)) {
            triggerSessionStale()
          }
          setEstimateErrorMessage(t('NLSOwnerConsoleCohortsErrorEstimate'))
        }
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [cohortId, isNew, canEdit, detailCohort, hasIncompleteRules, name, description, match, rules, emailsText, triggerSessionStale, t])

  const handleRuleFieldChange = (index: number, newField: CohortRuleField) => {
    setRules((prev) => {
      const next = [...prev]
      const currentRule = next[index]
      const allowedOps = FIELD_ALLOWED_OPS[newField]
      const nextOp = allowedOps.includes(currentRule.op) ? currentRule.op : allowedOps[0]
      let nextValue: unknown = 0
      if (newField === 'hasRestrictions') {
        nextValue = true
      } else if (newField === 'plan') {
        nextValue = nextOp === 'in' ? ['FREE_TIER'] : 'FREE_TIER'
      }
      next[index] = { field: newField, op: nextOp, value: nextValue }
      return next
    })
  }

  const handleRuleOpChange = (index: number, nextOp: CohortRuleOp) => {
    setRules((prev) => {
      const next = [...prev]
      const currentRule = next[index]
      let nextValue = currentRule.value
      if (currentRule.field === 'plan') {
        if (nextOp === 'in') {
          nextValue = []
        } else if (nextOp === 'eq') {
          nextValue = Array.isArray(nextValue) && nextValue[0] && TIER_IDS.includes(nextValue[0] as TierId)
            ? nextValue[0]
            : 'FREE_TIER'
        }
      }
      next[index] = { ...currentRule, op: nextOp, value: nextValue }
      return next
    })
  }

  const handleRuleValueChange = (index: number, nextValue: unknown) => {
    setRules((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], value: nextValue }
      return next
    })
  }

  const handleAddRule = () => {
    setRules((prev) => [...prev, { field: 'plan', op: 'eq', value: 'FREE_TIER' }])
  }

  const handleRemoveRule = (index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSave = async (reason: string) => {
    setIsSaving(true)
    setEditorErrorMessage(null)
    setEditorSuccessMessage(null)
    try {
      const emails = parseEmailsText(emailsText)
      const sanitizedRules = sanitizeCohortRules(rules)

      const res = await upsertOwnerCohort({
        id: isNew ? undefined : (cohortId || undefined),
        name: name.trim(),
        description: description.trim(),
        match,
        rules: sanitizedRules,
        includeEmails: emails,
        reason,
      })

      setEditorSuccessMessage(t('NLSOwnerConsoleCohortsSuccessSave'))
      if (isNew && res.cohort?.id) {
        navigateOwner({ view: 'config-cohort-detail', cohortId: res.cohort.id })
      }
    } catch (err) {
      if (isSessionStaleError(err)) {
        triggerSessionStale()
      }
      setEditorErrorMessage(t('NLSOwnerConsoleCohortsErrorSave'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleArchive = async (reason: string) => {
    if (!cohortId || isNew) return
    setIsArchiving(true)
    setEditorErrorMessage(null)
    setEditorSuccessMessage(null)
    try {
      await archiveOwnerCohort({ id: cohortId, reason })
      setEditorSuccessMessage(t('NLSOwnerConsoleCohortsSuccessArchive'))
      navigateOwner({ view: 'config-cohorts' })
    } catch (err) {
      if (isSessionStaleError(err)) {
        triggerSessionStale()
      }
      setEditorErrorMessage(t('NLSOwnerConsoleCohortsErrorArchive'))
    } finally {
      setIsArchiving(false)
    }
  }

  const handlePreview = async () => {
    if (!cohortId || isNew) return
    setIsPreviewLoading(true)
    setIsPreviewOpen(true)
    setEditorErrorMessage(null)
    try {
      const res = await previewOwnerCohortMembers({ id: cohortId, limit: 50 })
      setPreviewMembers(res.members)
    } catch (err) {
      if (isSessionStaleError(err)) {
        triggerSessionStale()
      }
      setEditorErrorMessage(t('NLSOwnerConsoleCohortsErrorPreview'))
      setIsPreviewOpen(false)
    } finally {
      setIsPreviewLoading(false)
    }
  }

  const renderRuleFieldLabel = (field: CohortRuleField): string => {
    switch (field) {
      case 'plan': return t('NLSOwnerConsoleCohortsFieldPlan')
      case 'signupAgeDays': return t('NLSOwnerConsoleCohortsFieldSignupAge')
      case 'lastActiveDays': return t('NLSOwnerConsoleCohortsFieldLastActive')
      case 'openTicketCount': return t('NLSOwnerConsoleCohortsFieldTickets')
      case 'workspacesOwnedCount': return t('NLSOwnerConsoleCohortsFieldWorkspaces')
      case 'personalCredits': return t('NLSOwnerConsoleCohortsFieldCredits')
      case 'hasRestrictions': return t('NLSOwnerConsoleCohortsFieldRestrictions')
    }
  }

  const renderRuleOpLabel = (op: CohortRuleOp): string => {
    switch (op) {
      case 'eq': return t('NLSOwnerConsoleCohortsOpEq')
      case 'in': return t('NLSOwnerConsoleCohortsOpIn')
      case 'lte': return t('NLSOwnerConsoleCohortsOpLte')
      case 'gte': return t('NLSOwnerConsoleCohortsOpGte')
    }
  }

  return (
    <div className="ownerCohortsPage">
      <div className="ownerCohortsHeader">
        <div>
          <h1>
            {isNew
              ? t('NLSOwnerConsoleCohortsCreateTitle')
              : t('NLSOwnerConsoleCohortsEditTitle', { name: detailCohort?.name || name || cohortId || '' })}
          </h1>
          <p>{t('NLSOwnerConsoleCohortsSubtitle')}</p>
        </div>
        <div className="ownerCohortsHeaderActions">
          <button
            type="button"
            className="ownerCohortsButton"
            onClick={() => navigateOwner({ view: 'config-cohorts' })}
          >
            {t('NLSOwnerConsoleCohortsBack')}
          </button>
        </div>
      </div>

      {!canEdit && (
        <div className="ownerCohortsNotice">
          {t('NLSOwnerConsoleCohortsReadOnlyNotice')}
        </div>
      )}

      {editorErrorMessage && (
        <div className="ownerCohortsAlert">{editorErrorMessage}</div>
      )}

      {editorSuccessMessage && (
        <div className="ownerCohortsSuccessAlert">{editorSuccessMessage}</div>
      )}

      <div className="ownerCohortsEditorCard">
        <div className="ownerCohortsFieldGroup">
          <label htmlFor="cohort-name-input">{t('NLSOwnerConsoleCohortsNameLabel')}</label>
          <input
            id="cohort-name-input"
            type="text"
            value={name}
            placeholder={t('NLSOwnerConsoleCohortsNamePlaceholder')}
            disabled={!canEdit || isDetailLoading}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="ownerCohortsFieldGroup">
          <label htmlFor="cohort-description-input">{t('NLSOwnerConsoleCohortsDescriptionLabel')}</label>
          <input
            id="cohort-description-input"
            type="text"
            value={description}
            placeholder={t('NLSOwnerConsoleCohortsDescriptionPlaceholder')}
            disabled={!canEdit || isDetailLoading}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="ownerCohortsFieldGroup">
          <label htmlFor="cohort-match-select">{t('NLSOwnerConsoleCohortsMatchLabel')}</label>
          <select
            id="cohort-match-select"
            value={match}
            disabled={!canEdit || isDetailLoading}
            onChange={(e) => setMatch(e.target.value as 'all' | 'any')}
          >
            <option value="all">{t('NLSOwnerConsoleCohortsMatchAll')}</option>
            <option value="any">{t('NLSOwnerConsoleCohortsMatchAny')}</option>
          </select>
        </div>

        <div className="ownerCohortsRulesSection">
          <div className="ownerCohortsRulesHeadingRow">
            <h3>{t('NLSOwnerConsoleCohortsRulesHeading')}</h3>
            {canEdit && (
              <button
                type="button"
                className="ownerCohortsButton"
                onClick={handleAddRule}
                disabled={isDetailLoading}
              >
                {t('NLSOwnerConsoleCohortsAddRule')}
              </button>
            )}
          </div>

          {rules.length === 0 ? (
            <p className="ownerCohortsFieldHelp">{t('NLSOwnerConsoleCohortsNoRules')}</p>
          ) : (
            rules.map((rule, idx) => {
              const allowedOps = FIELD_ALLOWED_OPS[rule.field]
              return (
                <div key={idx} className="ownerCohortsRuleRow">
                  <select
                    value={rule.field}
                    disabled={!canEdit || isDetailLoading}
                    aria-label={t('NLSOwnerConsoleCohortsFieldLabel')}
                    onChange={(e) => handleRuleFieldChange(idx, e.target.value as CohortRuleField)}
                  >
                    {ALL_FIELDS.map((f) => (
                      <option key={f} value={f}>
                        {renderRuleFieldLabel(f)}
                      </option>
                    ))}
                  </select>

                  <select
                    value={rule.op}
                    disabled={!canEdit || isDetailLoading}
                    aria-label={t('NLSOwnerConsoleCohortsOpLabel')}
                    onChange={(e) => handleRuleOpChange(idx, e.target.value as CohortRuleOp)}
                  >
                    {allowedOps.map((op) => (
                      <option key={op} value={op}>
                        {renderRuleOpLabel(op)}
                      </option>
                    ))}
                  </select>

                  {rule.field === 'hasRestrictions' ? (
                    <select
                      value={rule.value ? 'true' : 'false'}
                      disabled={!canEdit || isDetailLoading}
                      aria-label={t('NLSOwnerConsoleCohortsValueLabel')}
                      onChange={(e) => handleRuleValueChange(idx, e.target.value === 'true')}
                    >
                      <option value="true">{t('NLSOwnerConsoleCohortsValueTrue')}</option>
                      <option value="false">{t('NLSOwnerConsoleCohortsValueFalse')}</option>
                    </select>
                  ) : rule.field === 'plan' ? (
                    rule.op === 'in' ? (
                      <div className="ownerCohortsTierCheckboxGroup" aria-label={t('NLSOwnerConsoleCohortsValueLabel')}>
                        {TIER_IDS.map((tier) => {
                          const selectedTiers = Array.isArray(rule.value) ? (rule.value as string[]) : []
                          const isChecked = selectedTiers.includes(tier)
                          const tierKey = TIER_LABEL_KEYS[tier]
                          return (
                            <label key={tier} className="ownerCohortsTierCheckboxLabel">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={!canEdit || isDetailLoading}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...selectedTiers, tier]
                                    : selectedTiers.filter((t) => t !== tier)
                                  handleRuleValueChange(idx, next)
                                }}
                              />
                              <span>{tierKey ? t(tierKey) : tier}</span>
                            </label>
                          )
                        })}
                      </div>
                    ) : (
                      <select
                        value={typeof rule.value === 'string' && TIER_IDS.includes(rule.value as TierId) ? rule.value : 'FREE_TIER'}
                        disabled={!canEdit || isDetailLoading}
                        aria-label={t('NLSOwnerConsoleCohortsValueLabel')}
                        onChange={(e) => handleRuleValueChange(idx, e.target.value)}
                      >
                        {TIER_IDS.map((tier) => {
                          const tierKey = TIER_LABEL_KEYS[tier]
                          return (
                            <option key={tier} value={tier}>
                              {tierKey ? t(tierKey) : tier}
                            </option>
                          )
                        })}
                      </select>
                    )
                  ) : (
                    <input
                      type="number"
                      min="0"
                      value={typeof rule.value === 'number' ? rule.value : 0}
                      disabled={!canEdit || isDetailLoading}
                      aria-label={t('NLSOwnerConsoleCohortsValueLabel')}
                      onChange={(e) => handleRuleValueChange(idx, Number(e.target.value))}
                    />
                  )}

                  {canEdit && (
                    <button
                      type="button"
                      className="ownerCohortsButton ownerCohortsButtonDanger"
                      onClick={() => handleRemoveRule(idx)}
                      disabled={isDetailLoading}
                    >
                      {t('NLSOwnerConsoleCohortsRemoveRule')}
                    </button>
                  )}
                </div>
              )
            })
          )}
        </div>

        <div className="ownerCohortsFieldGroup">
          <label htmlFor="cohort-manual-emails-textarea">{t('NLSOwnerConsoleCohortsManualLabel')}</label>
          <textarea
            id="cohort-manual-emails-textarea"
            rows={4}
            value={emailsText}
            placeholder={t('NLSOwnerConsoleCohortsManualPlaceholder')}
            disabled={!canEdit || isDetailLoading}
            onChange={(e) => setEmailsText(e.target.value)}
          />
          <p className="ownerCohortsFieldHelp">{t('NLSOwnerConsoleCohortsManualHelp')}</p>
        </div>

        <div className="ownerCohortsLiveBanner">
          <span>
            {hasIncompleteRules
              ? t('NLSOwnerConsoleCohortsFinishRulesToEstimate')
              : estimateErrorMessage
                ? estimateErrorMessage
                : isEstimating
                  ? t('NLSOwnerConsoleCohortsLiveCountEstimating')
                  : t('NLSOwnerConsoleCohortsLiveCount', { count: estimatedCount ?? '—' })}
          </span>
        </div>

        <div className="ownerCohortsEditorActions">
          <div className="ownerCohortsEditorActionsLeft">
            {role === 'founder' && !isNew && (
              <button
                type="button"
                className="ownerCohortsButton"
                onClick={handlePreview}
                disabled={isDetailLoading || isPreviewLoading}
              >
                {isPreviewLoading ? t('NLSOwnerConsoleCohortsPreviewing') : t('NLSOwnerConsoleCohortsPreview')}
              </button>
            )}
          </div>

          <div className="ownerCohortsEditorActionsRight">
            {canEdit && !isNew && !isBuiltIn && (
              <button
                type="button"
                className="ownerCohortsButton ownerCohortsButtonDanger"
                onClick={() => setIsArchiveConfirmOpen(true)}
                disabled={isArchiving || isSaving || isDetailLoading}
              >
                {isArchiving ? t('NLSOwnerConsoleCohortsArchiving') : t('NLSOwnerConsoleCohortsArchive')}
              </button>
            )}

            {canEdit && (
              <button
                type="button"
                className="ownerCohortsButton ownerCohortsButtonPrimary"
                onClick={() => setIsSaveConfirmOpen(true)}
                disabled={isSaving || isArchiving || isDetailLoading || !name.trim() || hasIncompleteRules}
              >
                {isSaving ? t('NLSOwnerConsoleCohortsSaving') : t('NLSOwnerConsoleCohortsSave')}
              </button>
            )}
          </div>
        </div>
      </div>

      <OwnerFirmConfirm
        isOpen={isSaveConfirmOpen}
        title={t('NLSOwnerConsoleCohortsSaveConfirmTitle')}
        description={t('NLSOwnerConsoleCohortsSaveConfirmDesc', { name })}
        onClose={() => setIsSaveConfirmOpen(false)}
        onConfirm={async (reason) => {
          await handleSave(reason)
          setIsSaveConfirmOpen(false)
        }}
        confirmLabel={t('NLSOwnerConsoleDoIt')}
      />

      <OwnerFirmConfirm
        isOpen={isArchiveConfirmOpen}
        title={t('NLSOwnerConsoleCohortsArchiveConfirmTitle')}
        description={t('NLSOwnerConsoleCohortsArchiveConfirmDesc', { name })}
        onClose={() => setIsArchiveConfirmOpen(false)}
        onConfirm={async (reason) => {
          await handleArchive(reason)
          setIsArchiveConfirmOpen(false)
        }}
        confirmLabel={t('NLSOwnerConsoleDoIt')}
      />

      {isPreviewOpen && (
        <div className="ownerCohortsModalBackdrop" onClick={() => setIsPreviewOpen(false)}>
          <div className="ownerCohortsModalContent" onClick={(e) => e.stopPropagation()}>
            <div className="ownerCohortsModalHeader">
              <h2>{t('NLSOwnerConsoleCohortsPreviewTitle')}</h2>
              <button
                type="button"
                className="ownerCohortsButton"
                onClick={() => setIsPreviewOpen(false)}
              >
                {t('NLSOwnerConsoleCohortsPreviewClose')}
              </button>
            </div>
            <div className="ownerCohortsModalBody">
              {previewMembers.length === 0 ? (
                <p className="ownerCohortsFieldHelp">{t('NLSOwnerConsoleCohortsPreviewEmpty')}</p>
              ) : (
                <table className="ownerCohortsPreviewTable">
                  <thead>
                    <tr>
                      <th>{t('NLSOwnerConsoleCohortsPreviewColEmail')}</th>
                      <th>{t('NLSOwnerConsoleCohortsPreviewColPlan')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewMembers.map((m, idx) => (
                      <tr key={idx}>
                        <td>{m.email}</td>
                        <td>{m.plan}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function OwnerCohortsPanel({
  route,
}: {
  route: Extract<OwnerRoute, { view: 'config-cohorts' | 'config-cohort-detail' }>
}) {
  const { t } = useNls()
  const { role, can } = useRoles()
  const { triggerSessionStale } = useSessionLease()
  const canEdit = can('featureFlags:flip')

  const isDetail = route.view === 'config-cohort-detail'
  const cohortId = isDetail ? route.cohortId : null
  const isNew = cohortId === 'new'

  // Registry state
  const [cohorts, setCohorts] = useState<CohortListItem[]>([])
  const [showArchived, setShowArchived] = useState(false)
  const [listErrorMessage, setListErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (isDetail) return
    let active = true
    const run = async () => {
      try {
        const res = await listOwnerCohorts({ includeArchived: showArchived })
        if (active) {
          setCohorts(res.cohorts)
          setListErrorMessage(null)
        }
      } catch (err) {
        if (!active) return
        if (isSessionStaleError(err)) {
          triggerSessionStale()
        }
        setListErrorMessage(t('NLSOwnerConsoleCohortsErrorLoadList'))
      }
    }
    void run()
    return () => {
      active = false
    }
  }, [isDetail, showArchived, triggerSessionStale, t])

  const columns = useMemo<ColumnDef<CohortListItem>[]>(() => [
    {
      key: 'name',
      header: t('NLSOwnerConsoleCohortsColName'),
      render: (c) => (
        <span className="ownerCohortsTableName">{c.name}</span>
      ),
    },
    {
      key: 'status',
      header: t('NLSOwnerConsoleCohortsColStatus'),
      render: (c) => (
        <div className="ownerCohortsTableChips">
          {c.builtIn && (
            <span className="ownerCohortsChip ownerCohortsChipBuiltIn">
              {t('NLSOwnerConsoleCohortsBuiltInChip')}
            </span>
          )}
          {c.archived && (
            <span className="ownerCohortsChip ownerCohortsChipArchived">
              {t('NLSOwnerConsoleCohortsArchivedChip')}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'rules',
      header: t('NLSOwnerConsoleCohortsColRules'),
      render: (c) => (
        <span className="ownerCohortsTableRules">
          {formatCohortSummaryInWords(c, t)}
        </span>
      ),
    },
    {
      key: 'members',
      header: t('NLSOwnerConsoleCohortsColMembers'),
      render: (c) => (
        <span>{c.capped ? t('NLSOwnerConsoleCohortsLiveCountCapped') : String(c.memberCount)}</span>
      ),
    },
    {
      key: 'manual',
      header: t('NLSOwnerConsoleCohortsColManual'),
      render: (c) => <span>{String(c.manualCount)}</span>,
    },
  ], [t])

  if (isDetail) {
    return (
      <OwnerCohortEditor
        key={cohortId ?? 'new'}
        cohortId={cohortId}
        isNew={isNew}
        role={role}
        canEdit={canEdit}
        triggerSessionStale={triggerSessionStale}
      />
    )
  }

  // Registry view
  return (
    <div className="ownerCohortsPage">
      <div className="ownerCohortsHeader">
        <div>
          <h1>{t('NLSOwnerConsoleCohortsTitle')}</h1>
          <p>{t('NLSOwnerConsoleCohortsSubtitle')}</p>
        </div>
        <div className="ownerCohortsHeaderActions">
          {canEdit && (
            <button
              type="button"
              className="ownerCohortsButton ownerCohortsButtonPrimary"
              onClick={() => navigateOwner({ view: 'config-cohort-detail', cohortId: 'new' })}
            >
              {t('NLSOwnerConsoleCohortsNewCohort')}
            </button>
          )}
        </div>
      </div>

      {listErrorMessage && (
        <div className="ownerCohortsAlert">{listErrorMessage}</div>
      )}

      <div className="ownerCohortsControls">
        <label className="ownerCohortsCheckboxLabel">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <span>{t('NLSOwnerConsoleCohortsShowArchived')}</span>
        </label>
      </div>

      <OwnerTable
        data={cohorts}
        columns={columns}
        viewKey="cohorts"
        getRowId={(c) => c.id}
        onRowClick={(c) => navigateOwner({ view: 'config-cohort-detail', cohortId: c.id })}
        emptyState={
          <OwnerEmptyScaffold
            title={t('NLSOwnerConsoleCohortsEmptyTitle')}
            body={t('NLSOwnerConsoleCohortsEmptySubtitle')}
          />
        }
      />
    </div>
  )
}
